/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/naming-convention */
import Image from 'next/image';
import React, { useEffect, useState, useRef } from 'react';
import EmailVerificationPopUp from '../EMandateSuccess/EmailVerificationPopUp';
import EMandatePopUp from '../DigiLockerSuccess/EMandatePopUp';
import { screensMap } from '..';
import EsignSuccess from '../EMandateSuccess/EsignSuccess';
import EMandatePollingPopup from '../EMandateSuccess/EmandatePollingScreen';
import KycPopup from './KycPopup';
import { getPlanStatus, isLenderStepPending } from './helper';
import { useGlobalStore } from '@/store';
import CheckBox from '@/lib/CheckBox';
import Chip from '@/lib/Chip';
import Button from '@/lib/Button';
import { EmiPlans as EmiPlanType, LENDER_DETAILS_TYPE, LENDER_NAMES } from '@/api/types';
import { ESIGN_SUCCESS, LENDER_MAPPING } from '@/constants';
import { isNoCostEmi, roundToTwo } from '@/utils/helpers';
import OpenPopupLoader from '@/shared/Components/OpenPopupLoader';
import { PaymentCancelWrapper } from '@/shared/Components/PaymentCancelWrapper';
import { PopupFailureProps } from '@/shared/types';
import { getESignUrl } from '@/api/kyc';
import { getUserLoggedInStatus, trackHelper } from '@/analytics/utils';
import { ANALYTICS_ACTIONS, EVENT_NAMES } from '@/analytics/constants';

const EmiPlanCard = ({
  plan,
  selectPlan,
}: {
  plan: EmiPlanType;
  selectPlan: (plan: EmiPlanType) => void;
}): JSX.Element => {
  const planSelected = useGlobalStore((store) => store.selectedTenure);
  const [isActive, setIsActive] = useState(false);
  const { paymentDetails, lenderDetails, userAccessToken, user } = useGlobalStore();
  const { amount } = paymentDetails.data ?? {};
  const [userLenderStatus, setUserLenderStatus] = useState<LENDER_DETAILS_TYPE | undefined>();

  useEffect(() => {
    setIsActive(planSelected?.tenure === plan.tenure);
  }, [planSelected]);

  const handlePlanSelection = (): void => {
    selectPlan(plan);
    // Track handle plan selection
    trackHelper({
      actionName: ANALYTICS_ACTIONS.CLICK,
      objectName: EVENT_NAMES.EMI_PLAN_SELECTED,
      screen: screensMap.EMI_PLANS,
      properties: {
        fc_customer_id: user.id,
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        selected_plan: plan,
        user_status: getPlanStatus(lenderDetails, plan),
      },
    });
  };

  const handleMessage = (): string => {
    if (userLenderStatus && isLenderStepPending(userLenderStatus.kyc)) {
      return 'Complete KYC in next steps to be eligible for this offer';
    }

    if (userLenderStatus && isLenderStepPending(userLenderStatus.repayment)) {
      return 'KYC Completed. Set up your E-Mandate to be eligible for this offer';
    }

    if (userLenderStatus && isLenderStepPending(userLenderStatus.esign)) {
      return 'KYC and Repayment Setup completed. Complete E-Sign to be eligible for this offer';
    }

    return 'Pre-approved credit limit available. Complete OTP authentication to authorize this payment';
  };

  useEffect(() => {
    const status = getPlanStatus(lenderDetails, plan);
    setUserLenderStatus(status);
  }, []);

  return (
    <div
      aria-hidden
      onClick={handlePlanSelection}
      className={`${
        isActive ? 'border border-on-grey-normal rounded-lg' : ''
      } flex items-center bg-white-normal justify-between p-3 mb-4 last:mb-0`}
    >
      <div className="w-3/4">
        <p className="text-base text-on-grey-normal font-medium">
          ₹{plan.amount} x {plan.tenure}
        </p>
        {isNoCostEmi(plan.interest) ? (
          <p className="mt-1 text-positive-normal text-sm">No Cost EMI • ₹0 interest </p>
        ) : (
          <p className="mt-1 text-on-grey-subtle text-sm">
            Total interest is ₹
            {plan.total_interest_amount ? roundToTwo(plan.total_interest_amount) : 0} @{' '}
            {roundToTwo(plan.interest)}% p.a.
          </p>
        )}

        {isActive ? (
          <>
            <p className="text-sm mt-1 text-on-grey-subtle">
              {plan.fee
                ? `✦ A one-time processing fee of ₹${plan.fee} + GST will be charged by the lender`
                : '✦ No processing fee applicable'}{' '}
            </p>
            <div className="text-sm text-on-grey-normal font-normal mt-3 mb-1 flex items-center">
              <div className="border-surface-neutral-faded border w-fit p-1 rounded-full border-opacity-18 mr-2">
                <Image width={15} height={15} alt={plan.provider} src={`/${plan.provider}.svg`} />
              </div>
              Powered By {LENDER_MAPPING[plan.provider as LENDER_NAMES]}
            </div>
            <Chip className="[&&]:rounded-lg [&&]:border-0 [&&]:bg-surface-grey-subtle [&&]:items-start flex-col [&&]:py-1.5">
              {userLenderStatus?.available_limit && userLenderStatus.available_limit !== '0' ? (
                <p className="text-sm text-on-grey-subtle">
                  Available credit limit{' '}
                  <span className="text-on-grey-normal">
                    ₹{+userLenderStatus?.available_limit / 100}
                  </span>
                </p>
              ) : userLenderStatus?.approved_limit ? (
                <p className="text-sm text-on-grey-subtle">
                  Approved credit limit:{' '}
                  <span className="text-on-grey-normal">
                    ₹{+userLenderStatus?.approved_limit / 100}
                  </span>
                </p>
              ) : (
                ''
              )}
              <p className="text-xs font-bold text-on-grey-subtle">{handleMessage()}</p>
            </Chip>
          </>
        ) : (
          ''
        )}
      </div>
      <div>
        <CheckBox checked={plan.tenure === planSelected?.tenure} onClick={handlePlanSelection} />
      </div>
    </div>
  );
};

const EmiPlans = (): JSX.Element => {
  const { user, userAccessToken } = useGlobalStore();
  const emiPlans = useGlobalStore((store) => store.emiPlans);
  const setSelectedTenure = useGlobalStore((store) => store.setSelectedTenure);
  const planSelected = useGlobalStore((store) => store.selectedTenure);
  const { lenderDetails, setActiveView, setRepaymentId } = useGlobalStore();

  const [showKyc, setShowKyc] = useState(false);
  const eMandateUrlRef = useRef<string | null>(null);

  const [showEsignPopup, setShowEsign] = useState(false);
  const [showRepaymentPopup, setShowRepaymentPopup] = useState(false);
  const [openEmandate, setOpenEmandate] = useState(false);
  const [didEmandateFail, setDidEmandateFail] = useState(false);
  const [emandateError, setEmandateError] = useState('');
  const [openESignPopup, setOpenESignPopup] = useState(false);
  const [didESignFail, setDidESignFail] = useState(false);
  const [eSignError, setEsignError] = useState('');
  const [state, setState] = useState({
    showEsignSuccesss: false,
    showEmandatePollingLoader: false,
  });
  const eSignUrlRef = useRef<string | null>(null);

  const handlePlanSelection = (plan: EmiPlanType): void => {
    setSelectedTenure(plan);
  };

  const handleCTAClick = async (): Promise<void> => {
    if (!planSelected) {
      return;
    }

    // Track Emi plan selection CTA click
    trackHelper({
      actionName: ANALYTICS_ACTIONS.CLICK,
      objectName: EVENT_NAMES.EMI_PLANS_CTA_CLICK,
      screen: screensMap.EMI_PLANS,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        fc_customer_id: user.id,
        user_status: getPlanStatus(lenderDetails, planSelected),
        emi_plans: emiPlans,
        selected_plan: planSelected,
      },
    });
    const status = getPlanStatus(lenderDetails, planSelected);
    // If kyc is not done open selfie kyc screen
    if (status && isLenderStepPending(status.kyc)) {
      setShowKyc(true);
      return;
    }

    // if emandate step is pending show emandate popup
    if (status && isLenderStepPending(status.repayment)) {
      setShowRepaymentPopup(true);
      return;
    }

    // if esign step is pending show the esign popup
    if (status && isLenderStepPending(status.esign)) {
      if (!user.email) {
        setShowEsign(true);
        return;
      }

      await getESignUrl()
        .then((eSignResponse) => {
          if (eSignResponse.success && eSignResponse.url) {
            eSignUrlRef.current = eSignResponse.url;
            setOpenESignPopup(true);
            trackHelper({
              actionName: ANALYTICS_ACTIONS.RENDER,
              objectName: EVENT_NAMES.ESIGN_POPUP_RENDER,
              screen: screensMap.EMI_PLANS,
              properties: {
                contact: user.contact,
                user_type: getUserLoggedInStatus(userAccessToken, user.contact),
                fc_customer_id: user.id,
                user_status: getPlanStatus(lenderDetails, planSelected),
                emi_plans: emiPlans,
                selected_plan: planSelected,
              },
            });
          }
        })
        .catch(() => {
          setDidESignFail(true);
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (
      status &&
      !isLenderStepPending(status.kyc) &&
      !isLenderStepPending(status.esign) &&
      !isLenderStepPending(status.kyc)
    ) {
      setActiveView('PAYMENTS');
    }
  };

  const redirectToEmandate = async (url: string): Promise<void> => {
    eMandateUrlRef.current = url;
    setOpenEmandate(true);
    setShowRepaymentPopup(false);
  };

  const handleEmandateSuccess = (data: Record<string, unknown>): void => {
    if (data.success) {
      setOpenEmandate(false);
      // Set the emandate payment id in global store
      if (data.razorpay_payment_id) {
        setRepaymentId(data.razorpay_payment_id as string);
      }
      setState({
        ...state,
        showEmandatePollingLoader: true,
      });
    } else {
      setDidEmandateFail(true);
    }

    // Track Emandate Callback Success
    trackHelper({
      actionName: ANALYTICS_ACTIONS.BACKEND_RESPONSE,
      objectName: EVENT_NAMES.EMANDATE_CALLBACK_SUCCESS,
      screen: screensMap.DIGILOCKER_SUCCESS,
      properties: {
        contact: user.contact,
        selected_plan: planSelected,
        user_status: getPlanStatus(lenderDetails, planSelected as EmiPlanType),
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        data,
      },
    });
  };

  const handleEmandateFailure = (e: PopupFailureProps): void => {
    if (e.message) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setEmandateError((e.message as unknown as any)?.description || '');
    }
    setDidEmandateFail(true);
    setOpenEmandate(false);

    // Track Emandate Failure
    trackHelper({
      actionName: ANALYTICS_ACTIONS.RENDER,
      objectName: EVENT_NAMES.EMANDATE_POPUP_FAILURE,
      screen: screensMap.DIGILOCKER_SUCCESS,
      properties: {
        contact: user.contact,
        selected_plan: planSelected,
        user_status: getPlanStatus(lenderDetails, planSelected as EmiPlanType),
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
      },
    });
  };

  const handleEsignFailure = (e: PopupFailureProps): void => {
    setDidESignFail(true);
    setOpenESignPopup(false);
    if (e.message) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setEsignError((e.message as unknown as any)?.description || '');
    }

    // Track E-sign Failure
    trackHelper({
      actionName: ANALYTICS_ACTIONS.RENDER,
      objectName: EVENT_NAMES.ESIGN_POPUP_FAILURE,
      screen: screensMap.EMI_PLANS,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        fc_customer_id: user.id,
        selected_plan: planSelected,
        user_status: getPlanStatus(lenderDetails, planSelected as EmiPlanType),
        error_message: e.message ?? '',
      },
    });
  };

  useEffect(() => {
    // Track EMI plans rendered
    trackHelper({
      actionName: ANALYTICS_ACTIONS.RENDER,
      objectName: EVENT_NAMES.EMI_PLANS_RENDER,
      screen: screensMap.EMI_PLANS,
      properties: {
        fc_customer_id: user.id,
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        emi_plans: emiPlans,
        user_status: lenderDetails,
      },
    });
  }, []);

  const handleEsignSuccess = (data: Record<string, any>): void => {
    if (data.razorpay_payment_id) {
      setState({
        ...state,
        showEsignSuccesss: true,
      });
      setTimeout(() => {
        setActiveView('PAYMENTS');
      }, ESIGN_SUCCESS);
    } else {
      setDidESignFail(true);
      setOpenESignPopup(false);
    }
  };

  const { showEmandatePollingLoader } = state;

  return (
    <>
      <div className="d:relative d:h-full d:flex d:items-center d:justify-center">
        <div className="p-6 pt-3 h-full rounded-t-xl bg-white-normal mb-24 d:h-fit d:w-70 checkoutV2:w-[60%] checkoutV2:p-0 d:mb-0">
          <h3 className="font-tasa font-semibold text-2xl text-on-grey-normal">
            Select an EMI offer that suits you
          </h3>
          <div className="border-outline-grey-muted border rounded-xl border-opacity-18 mt-6">
            <div className="p-3 flex items-center">
              <Image width={30} height={30} alt="search" src="/flashcredit.svg" />
              <div className="ml-3">
                <p className="text-base font-medium text-on-grey-normal">Instant EMI</p>
                <p className="text-sm text-on-grey-subtle mt-1">
                  From trusted lenders. No paper work.
                </p>
              </div>
            </div>
            <div className="py-4 px-2 bg-surface-grey-subtle d:max-h-25 d:overflow-scroll checkoutV2:max-h-[240px]">
              {emiPlans.length ? (
                <>
                  {emiPlans.map((plan, key) => {
                    return <EmiPlanCard key={key} plan={plan} selectPlan={handlePlanSelection} />;
                  })}
                </>
              ) : null}
            </div>
          </div>
          <div className="absolute bottom-0 p-6 pt-2 w-full shadow-top bg-white-normal d:shrink-unset d:shadow-none d:p-0 d:relative d:mt-6 left-0">
            <Button onClick={handleCTAClick} className="w-full [&&]:my-0">
              Continue
            </Button>
          </div>
        </div>
      </div>
      {showKyc ? (
        <KycPopup
          isOpen={showKyc}
          onDismiss={(): void => {
            setShowKyc(false);
          }}
        />
      ) : null}
      {showEsignPopup ? <EmailVerificationPopUp /> : null}
      {showRepaymentPopup ? (
        <EMandatePopUp
          cb={redirectToEmandate}
          onDismiss={(): void => setShowRepaymentPopup(false)}
        />
      ) : null}
      {showEmandatePollingLoader ? (
        <EMandatePollingPopup
          isOpen={showEmandatePollingLoader}
          onDismiss={(): void => {
            setState((prev) => ({ ...prev, showEmandatePollingLoader: false }));
          }}
          onSuccess={(): void => {
            setState((prev) => ({
              ...prev,
              showEmandatePollingLoader: false,
            }));
            setActiveView('E_MANDATE_SUCCESS');
          }}
          onRetry={(): void => {
            setShowRepaymentPopup(true);
          }}
        />
      ) : null}
      {openEmandate ? (
        <OpenPopupLoader
          serviceType="emandate"
          onFail={handleEmandateFailure}
          serviceName="Razorpay"
          url={eMandateUrlRef.current as string}
          progressText="e-Mandate is in progress on"
          onSuccess={handleEmandateSuccess}
        />
      ) : null}
      {didEmandateFail ? (
        <PaymentCancelWrapper
          isCloseable={false}
          onDismiss={(): null => null}
          isOpen={didEmandateFail}
          ctaText="Change payment method"
          title="E-mandate Failed"
          description={emandateError || 'E-Mandate failed. Try with a different payment method'}
        />
      ) : null}
      {openESignPopup ? (
        <OpenPopupLoader
          serviceType="esign"
          onSuccess={handleEsignSuccess}
          onFail={handleEsignFailure}
          url={eSignUrlRef.current as string}
          serviceName="CashE"
          progressText="e-Sign is in progress on"
        />
      ) : null}
      {didESignFail ? (
        <PaymentCancelWrapper
          isOpen={didESignFail}
          onDismiss={(): null => null}
          ctaText="Change payment method"
          title="E-Sign Failed"
          isCloseable={false}
          description={eSignError || 'E-Sign failed. Try with a different payment method'}
        />
      ) : null}
      {state.showEsignSuccesss ? (
        <EsignSuccess
          isOpen={state.showEsignSuccesss}
          onDismiss={(): void => {
            setState({
              ...state,
              showEsignSuccesss: false,
            });
          }}
        />
      ) : (
        ''
      )}
    </>
  );
};

export default EmiPlans;
